<script setup lang="ts">
import { ref, reactive } from 'vue'

import { getAxios } from '../../api/axios'
import { useConfigStore } from '../../stores/config'
import ShowMore from '../shared/ShowMore.vue'
import Icon from '../shared/Icon.vue'

import type { VodSeasonWithEpisodesParagraph } from '../../utils/paragraph-schema/paragraphs'

type Episode = NonNullable<VodSeasonWithEpisodesParagraph['vodSeasonWithEpisodes']['episodes']>[number]

const props = withDefaults(defineProps<{
  vodSeasonWithEpisodes: VodSeasonWithEpisodesParagraph['vodSeasonWithEpisodes'],
  listOrderPosition?: number
}>(), {
  listOrderPosition: 0
})

const configStore = useConfigStore()

const isLoading = ref(false)
const selectedSeason = ref(props.vodSeasonWithEpisodes.seasonNumber ?? 1)
const currentSeason = reactive<{ seasonNumber: number, episodes: Episode[] }>({
  seasonNumber: props.vodSeasonWithEpisodes.seasonNumber ?? 1,
  episodes: props.vodSeasonWithEpisodes.episodes ?? []
})

const changeSeason = async () => {
  isLoading.value = true
  try {
    const axios = await getAxios()
    currentSeason.seasonNumber = selectedSeason.value
    const url = `${configStore.rsConfig.vodServiceUrl}/${props.vodSeasonWithEpisodes.seriesCapiId}/season/${selectedSeason.value}/with-episodes`
    const { data: { episodes } } = await axios.get(url)
    currentSeason.episodes = episodes
  } catch (error) {
    currentSeason.episodes = []
    console.error('Error loading episodes:', error)
  }
  isLoading.value = false
}

const getImageSrc = (stillPath?: string) => {
  return stillPath ? `https://image.tmdb.org/t/p/w92${stillPath}` : '/images/tvmovie/fallback-episodes-teaser.webp'
}
</script>

<template>
  <div class="bx-content bx-vod-episodes bx-typo--vod-epg bx-typo--vod-episodes bx-search">
    <h3>Die neuesten Episoden von "{{ vodSeasonWithEpisodes.seriesTitle }}"</h3>
    <form class="bx-form"
          :class="{ 'bx-js-loading': isLoading }">
      <div v-if="isLoading"
           class="bx-form__loader" />
      <div class="bx-form__select-wrapper">
        <select v-model="selectedSeason"
                class="bx-form__select"
                :disabled="isLoading || vodSeasonWithEpisodes.numberOfSeasons === 1"
                :aria-label="`Staffel auswählen für ${vodSeasonWithEpisodes.seriesTitle}`"
                @change="changeSeason()">
          <option v-for="i in vodSeasonWithEpisodes.numberOfSeasons"
                  :key="i"
                  :value="i"
                  :aria-label="`Staffel auswählen für ${vodSeasonWithEpisodes.seriesTitle}, insgesamt ${vodSeasonWithEpisodes.numberOfSeasons} Staffeln verfügbar`"
                  @keydown.tab.prevent="changeSeason()">
            Staffel {{ i }}
            <span v-if="vodSeasonWithEpisodes.numberOfSeasons === 1">
              von {{ vodSeasonWithEpisodes.numberOfSeasons }}
            </span>
          </option>
        </select>
        <Icon v-if="(vodSeasonWithEpisodes.numberOfSeasons ?? 0) > 1"
              name="arrow_toggle" />
      </div>
      <ShowMore v-if="currentSeason.episodes.length"
                :controls-id="`episodes-list-${listOrderPosition}`"
                content-label="Folgen"
                content-mode="list"
                :max-list-items="3">
        <ol>
          <li v-for="episode in currentSeason.episodes"
              :key="episode.number">
            <div>
              <figure>
                <img :src="getImageSrc(episode.stillPath)"
                     width="92"
                     height="52"
                     loading="lazy"
                     :alt="episode.title">
              </figure>
              <div>
                <div>
                  {{ episode.number }}. {{ episode.title }}
                </div>
                <div>
                  <span>Staffel {{ currentSeason.seasonNumber }} | Folge {{ episode.number }}/{{ currentSeason.episodes.length }}</span>
                  <span v-if="episode.runtime"> | {{ episode.runtime }} Minuten</span>
                </div>
              </div>
            </div>
            <div v-if="episode.description"
                 class="bx-typo--content-continuous-text">
              <p>{{ episode.description }}</p>
            </div>
          </li>
        </ol>
      </ShowMore>
      <div v-else
           class="bx-search__no-results">
        <span>
          <strong>Keine Episoden gefunden.</strong><br><br>
          Zu der ausgewählten Staffel konnten wir leider keine Episoden im System finden.
          Vielleicht ist diese Staffel noch nicht verfügbar oder wird in Kürze ergänzt.
        </span>
      </div>
    </form>
  </div>
</template>
